import {
  FaceLandmarker,
  FilesetResolver,
  type FaceLandmarkerResult,
} from '@mediapipe/tasks-vision';
import { BALANCE } from '../config/balance';
import { logWarn } from '../core/Debug';
import { HeadPoseFilter, type HeadPose, type HeadPoseFilterMode } from './HeadPoseFilter';
import {
  framePositionFromLandmarks,
  poseFromTransformationMatrix,
  separationFromLandmarks,
  viewFromLandmarks,
} from './HeadPose';

export type HeadTrackingStatus =
  'idle' | 'starting' | 'running' | 'no-face' | 'unavailable' | 'denied' | 'error';

export interface HeadTrackerOptions {
  /** Base URL of the vendored MediaPipe assets. */
  basePath?: string;
  /** Mirror the horizontal axis (selfie cameras are not mirrored by default). */
  mirrorX?: boolean;
  /**
   * Generate a pose instead of driving the webcam.
   *
   * Debug/demo only (`?headtrackdemo=1`). The generated samples go through the
   * same `HeadPoseFilter` as real ones, so the whole aim pipeline can be
   * exercised on a machine with no camera.
   */
  synthetic?: boolean;
  /**
   * Inference rate ceiling, Hz. Defaults to the balance value; the game lowers
   * it on phones, where the model shares the GPU with the renderer.
   */
  maxRate?: number;
  /**
   * MediaPipe delegate. Defaults to `CPU`: the GPU delegate opens its own
   * WebGL2 context next to the renderer's, and repeatedly creating those makes
   * the browser block the page with "Web page caused context loss". `GPU` is an
   * opt-in (`?headtrackgpu=1`) for machines with GPU headroom.
   */
  delegate?: 'CPU' | 'GPU';
  /**
   * Which smoother runs on the pose. Defaults to the constant-velocity Kalman;
   * `none` is `?nosmooth` and `oneEuro` is the `?leanlegacy` pipeline.
   */
  poseFilter?: HeadPoseFilterMode;
}

// Versioned so a stale browser cache (cached before the MIME fix) is never
// reused: changing the path forces a fresh fetch with `application/wasm`.
const WASM_PATH = 'assets/mediapipe/wasm-r2';
const MODEL_PATH = 'assets/mediapipe/face_landmarker.task';

/**
 * Webcam head tracking.
 *
 * Inference is rate limited to new video frames, runs entirely in the page
 * (self-hosted model and WASM, no network calls) and degrades to a disabled
 * feature whenever anything is missing. The game is fully playable without it.
 */
export class HeadTracker {
  private video: HTMLVideoElement | null = null;
  private stream: MediaStream | null = null;
  private landmarker: FaceLandmarker | null = null;
  private readonly filter: HeadPoseFilter;
  private lastVideoTime = -1;
  private lastInferenceMs = 0;
  private inferenceCostMs = 0;
  /** Inference rate ceiling, Hz. */
  private maxRate: number = BALANCE.headTracking.maxRate;
  /** The last measured head roll, radians, and its filtered value. */
  private headRollRaw = 0;
  private headRollValue = 0;
  /** The last head centre in the camera frame, 0..1, raw and unfiltered. */
  private framePosition: { x: number; y: number } | null = null;
  /** The last iris separation, raw; the viewing-distance estimate reads it. */
  private separation: number | null = null;
  /**
   * Iris offset -> metres. The fixed balance values by default; the screen
   * calibration swaps in a real-metre scale once the player has calibrated.
   */
  private readonly viewScale: { x: number; y: number } = {
    x: BALANCE.headTracking.viewScaleX,
    y: BALANCE.headTracking.viewScaleY,
  };

  status: HeadTrackingStatus = 'idle';
  reason = '';
  pose: HeadPose = { x: 0, y: 0, z: 0 };

  constructor(private readonly options: HeadTrackerOptions = {}) {
    if (options.maxRate && options.maxRate > 0) this.maxRate = options.maxRate;
    this.filter = new HeadPoseFilter({ mode: options.poseFilter });
  }

  get isActive(): boolean {
    return this.status === 'running' || this.status === 'no-face';
  }

  /** True when the pose is generated rather than measured. */
  get isSynthetic(): boolean {
    return this.options.synthetic === true;
  }

  get isCalibrated(): boolean {
    return this.filter.isCalibrated;
  }

  get lastInferenceDurationMs(): number {
    return this.inferenceCostMs;
  }

  /**
   * Where the head sits in the camera frame, `0..1` from the top-left, or null
   * when no face was seen. Raw and unfiltered: the calibration map draws this,
   * so it must not be smoothed into the offset the window uses.
   */
  get lastFramePosition(): { x: number; y: number } | null {
    return this.framePosition;
  }

  /** The last raw iris separation, 0..1 of the frame, or null with no face. */
  get lastSeparation(): number | null {
    return this.separation;
  }

  /**
   * Set the normalized-iris-to-metres scale, for screen calibration. Values
   * must be positive; a bad pair is ignored rather than disabling tracking.
   * Returns true when the scale actually changed, so the caller knows the
   * stored neutral pose is stale.
   */
  setViewScale(x: number, y: number): boolean {
    if (!(x > 0) || !(y > 0)) return false;
    if (x === this.viewScale.x && y === this.viewScale.y) return false;
    this.viewScale.x = x;
    this.viewScale.y = y;
    return true;
  }

  /** Start the camera and the landmarker. Returns false when unavailable. */
  async enable(): Promise<boolean> {
    if (this.status === 'starting' || this.isActive) return this.isActive;

    this.status = 'starting';
    this.reason = '';
    this.headRollRaw = 0;
    this.headRollValue = 0;

    if (this.options.synthetic) {
      this.filter.reset();
      // Pin the neutral pose so the generated motion is symmetric rather than
      // being offset by whatever the first sample happened to be.
      this.filter.calibrate({ x: 0, y: 0, z: 0 });
      this.lastInferenceMs = 0;
      this.lastVideoTime = -1;
      this.inferenceCostMs = 0;
      this.status = 'running';
      return true;
    }

    if (typeof window === 'undefined' || !window.isSecureContext) {
      this.status = 'unavailable';
      this.reason = 'Head tracking needs a secure context (localhost or HTTPS).';
      return false;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      this.status = 'unavailable';
      this.reason = 'This browser does not expose a camera API.';
      return false;
    }

    try {
      // The off-axis window only needs the viewer position, so a small frame is
      // enough and keeps inference cheap enough for the 60 Hz gate.
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 320, height: 240, facingMode: 'user' },
        audio: false,
      });
    } catch (error) {
      const name = error instanceof DOMException ? error.name : '';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        this.status = 'denied';
        this.reason = 'Camera permission was declined. The game works without it.';
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        this.status = 'unavailable';
        this.reason = 'No camera was found. The game works without it.';
      } else if (name === 'NotReadableError' || name === 'TrackStartError') {
        this.status = 'error';
        this.reason = 'The camera is in use by another app. The game works without it.';
      } else if (name === 'OverconstrainedError') {
        this.status = 'unavailable';
        this.reason = 'The camera does not support the requested video mode.';
      } else {
        this.status = 'denied';
        this.reason = 'The camera could not be opened. The game works without it.';
      }
      logWarn(`head tracking: ${this.reason}`);
      return false;
    }

    try {
      const fileset = await FilesetResolver.forVisionTasks(WASM_PATH);
      this.landmarker = await this.createLandmarker(fileset);
    } catch (error) {
      logWarn('head tracking: the landmarker could not be created', error);
      this.teardownStream();
      this.status = 'error';
      this.reason = 'The head tracking model could not be loaded. The game works without it.';
      return false;
    }

    this.video = document.createElement('video');
    this.video.playsInline = true;
    this.video.muted = true;
    this.video.srcObject = this.stream;
    await this.video.play().catch(() => undefined);

    this.filter.reset();
    this.status = 'running';
    this.reason = '';
    return true;
  }

  /**
   * Create the landmarker with the chosen delegate.
   *
   * The default is CPU, which keeps MediaPipe out of the WebGL context pool: a
   * GPU delegate opens a second WebGL2 context beside the renderer's, and the
   * browser blocks the page once too many are created or lost. A GPU request
   * falls back to CPU rather than failing.
   */
  private async createLandmarker(
    fileset: Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>>,
  ): Promise<FaceLandmarker> {
    const preferred = this.options.delegate ?? 'CPU';
    const build = (delegate: 'CPU' | 'GPU') =>
      FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL_PATH, delegate },
        runningMode: 'VIDEO',
        numFaces: 1,
        outputFaceBlendshapes: false,
        outputFacialTransformationMatrixes: true,
      });
    if (preferred === 'GPU') {
      try {
        return await build('GPU');
      } catch (gpuError) {
        logWarn('head tracking: GPU delegate unavailable, using CPU', gpuError);
      }
    }
    return build('CPU');
  }

  /** Stop the camera and release the model. */
  disable(): void {
    this.teardownStream();
    this.landmarker?.close();
    this.landmarker = null;
    this.filter.reset();
    this.pose = { x: 0, y: 0, z: 0 };
    this.headRollRaw = 0;
    this.headRollValue = 0;
    this.framePosition = null;
    this.separation = null;
    this.status = 'idle';
    this.reason = '';
  }

  private teardownStream(): void {
    if (this.stream) {
      for (const track of this.stream.getTracks()) track.stop();
      this.stream = null;
    }
    if (this.video) {
      this.video.srcObject = null;
      this.video = null;
    }
  }

  /** Capture the current pose as neutral. */
  recalibrate(): void {
    this.filter.calibrate(this.pose);
  }

  /** Change the inference rate ceiling, Hz, while the feature is running. */
  setMaxRate(rate: number): void {
    if (rate > 0) this.maxRate = rate;
  }

  /**
   * How far the head is rolled from upright, radians, filtered.
   *
   * The off-axis window only needs the viewer's position, so this is the one
   * piece of head rotation the game reads: a deliberate tilt is the gesture
   * that settles the camera onto a new wall. The sign convention of the facial
   * transform matrix still needs a real webcam to confirm (`Q6`), so consumers
   * should treat it as a magnitude unless they have verified the sign.
   */
  get headRoll(): number {
    return this.headRollValue;
  }

  /**
   * Poll for a new pose. Returns the filtered offset, or null when no new
   * frame was processed this tick.
   */
  update(now: number, dt: number): HeadPose | null {
    if (this.isSynthetic) return this.updateSynthetic(now, dt);
    if (!this.landmarker || !this.video) return null;
    // No inference for a hidden tab; the game simulation is paused anyway.
    if (typeof document !== 'undefined' && document.hidden) return null;

    const minimumInterval = 1000 / this.maxRate;
    if (now - this.lastInferenceMs < minimumInterval) return null;

    const video = this.video;
    if (video.readyState < 2) return null;
    if (video.currentTime === this.lastVideoTime) return null;
    this.lastVideoTime = video.currentTime;

    this.lastInferenceMs = now;

    let result: FaceLandmarkerResult;
    const start = performance.now();
    try {
      result = this.landmarker.detectForVideo(video, now);
    } catch (error) {
      logWarn('head tracking: inference failed', error);
      this.status = 'error';
      this.reason = 'Head tracking stopped working and has been disabled.';
      return null;
    }
    // Full model cost, not just the pose extraction.
    this.inferenceCostMs = performance.now() - start;

    // The head roll comes from the facial transform matrix. It is tracked
    // before the iris check so a tilt still registers when the iris landmarks
    // are lost, and it is filtered here rather than by the position filter,
    // which owns the viewer offset only.
    this.trackHeadRoll(result, dt);

    const landmarks = result.faceLandmarks?.[0];
    this.framePosition = framePositionFromLandmarks(landmarks);
    this.separation = separationFromLandmarks(landmarks);
    const pose = this.extractPose(result);

    if (!pose) {
      this.status = 'no-face';
      return null;
    }

    this.status = 'running';
    this.pose = pose;
    return this.filter.update(pose, dt);
  }

  /** Update the filtered head roll from this frame's transform matrix. */
  private trackHeadRoll(result: FaceLandmarkerResult, dt: number): void {
    const rotation = poseFromTransformationMatrix(result.facialTransformationMatrixes?.[0]?.data, {
      mirrorX: this.options.mirrorX,
    });
    if (rotation) this.headRollRaw = rotation.roll;
    const t = 1 - Math.exp(-BALANCE.headTracking.rollDamping * dt);
    this.headRollValue += (this.headRollRaw - this.headRollValue) * t;
  }

  /**
   * A slow, deterministic head motion for the camera-free demo.
   *
   * The amplitudes are large enough to reach the clamps, so the dead zone,
   * the limits and the smoothing all show up. The 30 Hz gate is shared with
   * the real path.
   */
  private updateSynthetic(now: number, dt: number): HeadPose | null {
    if (!this.isActive) return null;
    const minimumInterval = 1000 / this.maxRate;
    if (now - this.lastInferenceMs < minimumInterval) return null;
    this.lastInferenceMs = now;

    const t = now / 1000;
    const raw: HeadPose = {
      x: Math.sin(t * 0.6) * 0.9,
      y: Math.sin(t * 0.8 + 2) * 0.5,
      z: Math.sin(t * 0.5) * 0.3,
    };
    this.pose = raw;
    // A frame position for the calibration map, driven by the same motion so a
    // camera-free demo still draws a moving blob.
    this.framePosition = {
      x: Math.min(1, Math.max(0, 0.5 - raw.x * 0.3)),
      y: Math.min(1, Math.max(0, 0.5 - raw.y * 0.3)),
    };
    // A separation consistent with the generated depth, so the distance
    // estimate has something to read in the camera-free demo too.
    this.separation = raw.z > 1e-4 ? BALANCE.headTracking.viewDistance / raw.z : null;
    // A roll that sweeps past the tilt threshold every few seconds, so the
    // camera-free demo can exercise the plane swivel too.
    this.headRollRaw = Math.sin(t * 1.1) * 0.9;
    const rollT = 1 - Math.exp(-BALANCE.headTracking.rollDamping * dt);
    this.headRollValue += (this.headRollRaw - this.headRollValue) * rollT;
    return this.filter.update(raw, dt);
  }

  /**
   * Read only the viewer position from the face landmarks.
   *
   * Position and roll are read separately: the off-axis window needs the
   * viewer's translation, and the camera swivel needs the head's roll, so the
   * two are extracted from their own parts of the result and filtered apart.
   */
  private extractPose(result: FaceLandmarkerResult): HeadPose | null {
    return viewFromLandmarks(result.faceLandmarks?.[0], {
      mirrorX: this.options.mirrorX,
      scaleX: this.viewScale.x,
      scaleY: this.viewScale.y,
    });
  }
}
